import { useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TreePine, AlertTriangle, Plus } from 'lucide-react';
import { fetchTreeQuota, listTrees } from '@/api/trees.api';
import { TreeFormModal } from '@/components/Layout/TreeFormModal';
import { GemeinschaftWegweiser } from '@/components/Gemeinschaft/GemeinschaftWegweiser';
import { refresh } from '@/api/auth.api';
import { useAuthStore } from '@/store/useAuthStore';
import { useTreeContextStore } from '@/store/useTreeContextStore';

// ─── TreeGuard ──────────────────────────────────────────────────────────────
//
// Stellt sicher, dass VOR dem Rendern datenführender Seiten ein gültiger
// Stammbaum aktiv ist:
//   1. Bäume laden, auf die der Nutzer Zugriff hat.
//   2. Gemerkte Auswahl prüfen; ungültig/leer → ersten Baum wählen.
//   3. Nach der Auswahl /auth/me neu laden – die Berechtigungen gelten JE
//      Stammbaum und sind direkt nach dem Login noch leer.
//
// Ohne diesen Schritt liefen alle Requests in 400 TREE_REQUIRED bzw. die
// Oberfläche wäre komplett ausgegraut.

export function TreeGuard() {
  const queryClient = useQueryClient();
  const { activeTreeId, setActiveTree } = useTreeContextStore();
  const setAuth = useAuthStore((s) => s.setAuth);
  const accessToken = useAuthStore((s) => s.accessToken);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [zeigeFormular, setZeigeFormular] = useState(false);

  const {
    data: trees,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['trees', 'accessible'],
    queryFn: listTrees,
    staleTime: 60_000,
  });

  // Nur nachfragen, wenn gar kein Baum da ist: sonst hat die Antwort keine
  // Folge, und der Normalfall soll keine zusätzliche Anfrage kosten.
  const { data: kontingent } = useQuery({
    queryKey: ['trees', 'quota'],
    queryFn: fetchTreeQuota,
    enabled: !!trees && trees.length === 0,
    staleTime: 30_000,
  });

  // Solange die Antwort fehlt, den Weg offen lassen: ein Knopf, der in eine
  // ehrliche Fehlermeldung läuft, ist besser als eine Sackgasse, die keine ist.
  const darfAnlegen = !kontingent || kontingent.unbegrenzt || kontingent.remaining > 0;

  // Auswahl herstellen bzw. korrigieren
  useEffect(() => {
    if (!trees) return;
    const stillValid = activeTreeId && trees.some((t) => t.id === activeTreeId);
    if (!stillValid) {
      setActiveTree(trees.length > 0 ? trees[0].id : null);
      setPermissionsLoaded(false);
    }
  }, [trees, activeTreeId, setActiveTree]);

  // Rechte des aktiven Baums nachladen (Login liefert sie noch nicht mit)
  useEffect(() => {
    if (!activeTreeId || !accessToken || permissionsLoaded) return;
    let cancelled = false;

    // /auth/refresh liefert den Nutzer inkl. der Rechte FÜR DEN AKTIVEN BAUM
    // (der Interceptor sendet X-Tree-Id mit).
    refresh()
      .then(({ accessToken: token, csrfToken: csrf, user }) => {
        if (!cancelled) {
          setAuth(token, user, csrf);
          setPermissionsLoaded(true);
        }
      })
      .catch(() => {
        // Fehlschlag hier darf die App nicht blockieren – der Nutzer sieht
        // dann ggf. weniger Features und kann neu laden.
        if (!cancelled) setPermissionsLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [activeTreeId, accessToken, permissionsLoaded, setAuth]);

  // Baumwechsel → Daten des alten Baums verwerfen UND neu laden.
  //
  // WICHTIG: resetQueries statt removeQueries. removeQueries wirft aktive
  // Queries aus dem Cache, ohne einen Neuabruf auszulösen – die bereits
  // gemounteten Beobachter (z. B. der Stammbaum-Canvas) blieben dann
  // dauerhaft im Ladezustand hängen, bis die Seite neu geladen wurde.
  // resetQueries setzt sie zurück und ruft aktive Queries direkt neu ab.
  //
  // Der Ref-Vergleich sorgt dafür, dass nur ein echter Wechsel zurücksetzt
  // (nicht schon der erste Render).
  const previousTreeId = useRef<string | null>(null);
  useEffect(() => {
    if (!activeTreeId) return;
    const previous = previousTreeId.current;
    previousTreeId.current = activeTreeId;
    if (previous === null || previous === activeTreeId) return;

    // Rechte gelten je Stammbaum → für den neuen Baum neu holen.
    setPermissionsLoaded(false);
    void queryClient.resetQueries({ predicate: (q) => q.queryKey[0] !== 'trees' });
  }, [activeTreeId, queryClient]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <span className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
          <p className="mt-3 text-sm text-slate-500">Stammbäume werden geladen...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <CenteredNotice
        icon={<AlertTriangle className="h-10 w-10 text-amber-500" aria-hidden="true" />}
        title="Stammbäume konnten nicht geladen werden"
        text={error instanceof Error ? error.message : 'Bitte lade die Seite neu.'}
      />
    );
  }

  // Kein Zugriff auf irgendeinen Baum. Das ist seit der Selbstverwaltung kein
  // Sackgassen-Zustand mehr: wer darf, legt hier einfach einen an. Nur wer sein
  // Kontingent ausgeschöpft hat oder auf eine Freigabe wartet, muss fragen.
  if (!trees || trees.length === 0) {
    return (
      <>
        <CenteredNotice
          icon={<TreePine className="h-10 w-10 text-slate-400" aria-hidden="true" />}
          title="Noch kein Stammbaum"
          text={
            darfAnlegen
              ? 'Legen Sie Ihren ersten Stammbaum an – Sie werden sein Besitzer. Oder warten Sie darauf, zu einem bestehenden hinzugefügt zu werden.'
              : 'Für dieses Konto ist noch kein Stammbaum freigeschaltet, und das Kontingent für eigene ist ausgeschöpft. Bitte wenden Sie sich an den Betreiber.'
          }
          aktion={
            <>
              {darfAnlegen && (
                <button
                  onClick={() => setZeigeFormular(true)}
                  className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  Stammbaum anlegen
                </button>
              )}
              {/* Der einzige Ort, an dem dieses Konto die Gemeinschaft noch
                  angeboten bekommt: Die Navigation liegt hinter diesem Guard,
                  und wer hier steht, kommt nicht daran vorbei. Der Wegweiser
                  erscheint von selbst nur, wenn der Tarif sie enthält. */}
              <GemeinschaftWegweiser />
            </>
          }
        />
        {zeigeFormular && <TreeFormModal tree={null} onClose={() => setZeigeFormular(false)} />}
      </>
    );
  }

  // Auswahl läuft noch (erster Render nach dem Laden) – oder die Rechte des
  // gewählten Baums sind noch unterwegs.
  //
  // Auf die Rechte MUSS gewartet werden: nach dem Anmelden sind sie leer (der
  // Login kennt den aktiven Baum noch nicht). Wer hier schon durchlässt, zeigt
  // dem Nutzer für einen Moment „Kein Zugriff" – RequirePermission sieht dann
  // eine leere Rechteliste und leitet um. Genau das passierte bei jeder ersten
  // Anmeldung auf einem frischen Gerät.
  if (!activeTreeId || (accessToken && !permissionsLoaded)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <span className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
      </div>
    );
  }

  return <Outlet />;
}

// ─── Hinweis-Seite ──────────────────────────────────────────────────────────

function CenteredNotice({
  icon,
  title,
  text,
  aktion,
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
  aktion?: React.ReactNode;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="max-w-md rounded-lg border border-slate-200 bg-white p-8 text-center shadow-sm">
        <div className="flex justify-center">{icon}</div>
        <h1 className="mt-4 text-lg font-semibold text-slate-900">{title}</h1>
        <p className="mt-2 text-sm leading-relaxed text-slate-600">{text}</p>
        {aktion}
      </div>
    </div>
  );
}
